import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { useLang } from "@/i18n/LanguageContext";
import * as LucideIcons from "lucide-react";
import { Sparkles } from "lucide-react";

const COPY = {
  en: { label: "What we do", title: "Services", from: "from", revisions: "3 revisions included", custom_title: "Custom & R&D", custom_desc: "Bespoke scopes, residency, licensing — write to us.", counter_services: "services", counter_custom: "custom", scoped_note: "Pricing is scoped per project" },
  ru: { label: "Что мы делаем", title: "Услуги", from: "от", revisions: "3 правки включены", custom_title: "Custom & R&D", custom_desc: "Индивидуальные скоупы, резиденция, лицензирование — напишите нам.", counter_services: "услуг", counter_custom: "кастом", scoped_note: "Стоимость рассчитываем под задачу" },
  es: { label: "Qué hacemos", title: "Servicios", from: "desde", revisions: "3 revisiones incluidas", custom_title: "Custom & R&D", custom_desc: "Scopes a medida, residencia, licencias — escríbenos.", counter_services: "servicios", counter_custom: "personalizado", scoped_note: "El costo se calcula según el proyecto" },
};

export default function Services() {
  const { lang } = useLang();
  const t = COPY[lang] || COPY.en;
  const [items, setItems] = useState([]);

  useEffect(() => {
    api.get("/services").then(({ data }) => setItems(data)).catch(() => setItems([]));
  }, []);

  const pick = (obj, fallback = "") =>
    (obj && (obj[lang] || obj.en || obj.ru || obj.es)) || fallback;

  return (
    <section id="services" className="relative bg-white py-24 lg:py-32 border-t border-[#0a0a0a]">
      <div className="max-w-[1600px] mx-auto px-6 lg:px-12">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8 mb-16">
          <div>
            {/* kicker desconectado 22-09-2026 (Ksenia pidio quitarlo). Reactivar: poner el flag en true */}
            {false && (
              <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#8a8a8a] mb-3">· {t.label}</div>
            )}
            <h2 className="font-mono font-bold tracking-tighter text-3xl sm:text-4xl lg:text-5xl text-[#0a0a0a]">
              {t.title}
            </h2>
          </div>
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#8a8a8a]">
            {String(items.length).padStart(2, "0")} / {t.counter_services}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-[#0a0a0a] border border-[#0a0a0a]">
          {items.map((s, i) => {
            const Icon = LucideIcons[s.icon] || Sparkles;
            return (
              <motion.div
                key={s.service_id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.6, delay: (i % 2) * 0.08 }}
                className="group relative bg-white p-8 lg:p-10 hover:bg-[#f5f5f5] transition-colors"
              >
                <div className="flex items-start justify-between gap-4 mb-6">
                  <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#8a8a8a]">
                    {String(i + 1).padStart(2, "0")}
                  </div>
                  <div className="w-10 h-10 border border-[#0a0a0a] flex items-center justify-center group-hover:bg-[#00d4ff] group-hover:border-[#00d4ff] transition-colors">
                    <Icon size={18} className="text-[#0a0a0a]" />
                  </div>
                </div>

                <h3 className="font-mono font-medium text-xl sm:text-2xl tracking-tight text-[#0a0a0a]">
                  {pick(s.title, "Service")}
                </h3>
                <p className="mt-4 font-sans text-base text-[#4a4a4a] leading-relaxed max-w-md">
                  {pick(s.description, "")}
                </p>

                {/* precios y "3 revisiones incluidas" desconectados 22-09-2026:
                    el estudio no publica precios (se cotiza por proyecto) y las
                    revisiones ya estan en el contrato. Reactivar: flag en true */}
                {false && (
                  <div className="mt-8 flex items-end justify-between gap-4 pt-6 border-t border-dashed border-[#e5e5e5]">
                    <div>
                      <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#8a8a8a]">{t.from}</div>
                      <div className="mt-1 font-mono text-2xl font-bold tracking-tighter text-[#0a0a0a]">
                        ${Number(s.price || 0).toLocaleString()}
                        <span className="text-[#ff006e]">+</span>
                      </div>
                    </div>
                    <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#0a0a0a] border border-[#0a0a0a] px-3 py-1.5">
                      {t.revisions}
                    </div>
                  </div>
                )}
              </motion.div>
            );
          })}
          {items.length % 2 === 1 && (
            <div className="hidden md:block bg-white p-8 lg:p-10 relative overflow-hidden">
              <div className="absolute inset-0 opacity-[0.08]">
                <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg">
                  <defs>
                    <pattern id="dots" width="20" height="20" patternUnits="userSpaceOnUse">
                      <circle cx="2" cy="2" r="1" fill="#0a0a0a" />
                    </pattern>
                  </defs>
                  <rect width="100%" height="100%" fill="url(#dots)" />
                </svg>
              </div>
              <div className="relative h-full flex flex-col justify-between">
                <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#8a8a8a]">
                  {String(items.length + 1).padStart(2, "0")} / {t.counter_custom}
                </div>
                <div>
                  <h3 className="font-mono font-medium text-xl text-[#0a0a0a]">{t.custom_title}</h3>
                  <p className="mt-2 font-sans text-sm text-[#4a4a4a]">{t.custom_desc}</p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* nota unica bajo la grilla (pedido del estudio 22-09-2026):
            no publicamos precios, se cotiza el volumen de produccion */}
        <p className="mt-10 lg:mt-12 font-mono text-[11px] uppercase tracking-[0.22em] text-[#8a8a8a]">
          {t.scoped_note}
        </p>
      </div>
    </section>
  );
}
